import {cloneDeep} from 'lodash';
import api from '@/api';

const Download = ({
    state = {
        customizations: {},
        customizationsOrder: [],
        types: [],
    },
    content,
} = {}) => {
    return {
        content: content,
        customizations: state.customizations,
        customizationsOrder: state.customizationsOrder,
        availableOptions: state.availableOptions,
        types: state.types,
        typeText: null,
        customizationsText: {},
        worksheet: null,
        previews: [],
        placementLogoCoordinates: {},
        selected: {},
        userHasPremium: state.user.hasPremium,
        userBrandingPreference: state.user.brandingPreference,
        userLogo: state.user.logo,
        learnPremiumOptions: !state.user.hasPremium,
        requiresPremium: false,
        availableLanguages: state.availableLanguages,
        unsupportedBrowser: false,
        isPolling: false,

        interval: null,
        times: 0,
        loading: false,

        pollInFlight: false,
        downloadStarted: false,
        currentPollRunId: 0,

        get isDesktop() { return window.innerWidth >= 1280 },
        get pages(){
            const previews = typeof this.previews === 'object' ? this.previews : [];
            return Object.keys(previews).length;
        },
        get hasPreviews(){
            return this.pages > 0;
        },

        init() {
            const { files, filePath } = this.initializeVariables();

            this.$watch('worksheet', () => {
                const isPremiumOptionSelected = this.isPremiumOptionSelected(this.selected);

                this.requiresPremium = !this.userHasPremium && isPremiumOptionSelected;
                this.learnPremiumOptions = !this.userHasPremium;

                window.dispatchEvent(new CustomEvent('premium-state-changed', {
                    detail: {
                        requiresPremium: this.requiresPremium,
                        userHasPremium: this.userHasPremium
                    }
                }));

                this.handlePreviewUpdate(files, filePath);
            });
        },

        setOption(object = { customization: '', option: '', default: false, premium: false }){
            const { option, customization } = object;

            const selected = this.selected.map((item) => {
                const [key] = Object.keys(item);

                if(key === customization){
                    item[key] = option;
                }

                return item;
            });

            const { type: selectedType } = this.getTypeProperty(selected);

            const type = this.getTypeBy((item) => item.type === selectedType);
            const { text, files } = type;

            const filePath = this.getOptionPath(selected);

            this.selected = selected;
            this.worksheet = files[filePath];
            this.typeText = text;

            this.handleSelectedUpdated(selected);
        },

        handleSelectedUpdated(selected){
            selected.forEach((item) => {
                const [key] = Object.keys(item);

                if(typeof item['type'] === 'string' || typeof this.customizations[key] === 'undefined'){
                    return;
                }

                const { text } = this.customizations[key].find(({ option }) => item[key] === option);

                this.customizationsText[key] = text;
            });
        },

        /** Also used by `Branding/UploadLogo.js` */
        setBrandingPreference(brandingPreference) {
            const brandingOption = this.customizations.branding?.find(({ option }) => option === brandingPreference);

            if (!brandingOption) return;

            const option = { customization: 'branding', option: brandingOption.option, premium: brandingOption.premium };

            if (this.isOptionSelected(option)) return;

            !this.isDisabledOption(option) && this.setOption(option);
        },

        handlePreviewUpdate(files, filePath) {
            const defaultWorksheet = files[filePath];
            const selected = cloneDeep(this.selected);
            const { file } = this.handleAlternativeWorksheetPreview(selected);

            let preview = defaultWorksheet;

            if(typeof file !== 'undefined') {
                preview = file;
            }

            if(this.unsupportedBrowser){
                preview = null;
            }

            const { previews, placementLogoCoordinates } = preview;

            this.previews = previews ?? [];
            this.placementLogoCoordinates = placementLogoCoordinates ?? {};
        },

        /** Reset the type to standard, keeping the selected state from others. */
        handleAlternativeWorksheetPreview(selected){
            const { type: standardType, files } = this.getTypeBy((item) => item.default);

            const mutatedSelected = selected.map((item) => {
                const [key] = Object.keys(item);

                if(key === 'type'){
                    item[key] = standardType;
                }

                return item;
            });

            const filePath = this.getOptionPath(mutatedSelected);

            return { file: files[filePath] };
        },

        isOptionSelected(object = { customization: '', option: '' }){
            const { customization, option } = object;

            return this.selected.some((item) => item[customization] && item[customization] === option);
        },

        isDisabledOption(object = { customization: '', option: '' }){
            const { option, customization } = object;

            const selected = cloneDeep(this.selected);

            const index = selected.findIndex(item => {
                const [key] = Object.keys(item);
                return key === customization;
            });

            if (index !== -1) {
                selected[index] = { [customization]: option };
            }

            const { type: selectedType } = this.getTypeProperty(selected);

            const { files } = this.getTypeBy(item => item.type === selectedType);

            const filePath = this.getOptionPath(selected);

            return typeof files[filePath] === 'undefined';
        },

        getSimilarOptionBasedOnSelected(availableOptions, similarOject = { customization: '', option: '' }){
            let index = 0;

            if(availableOptions.length === 1){
                return index;
            }

            const { customization, option } = similarOject;

            const selected = cloneDeep(this.selected);

            const perfectMatches = selected.map(item => {
                const [key] = Object.keys(item);
                return key === customization ? option : item[key];
            }).join('|');

            const splittedPerfectMatches = perfectMatches.split('|');
            const splittedAvailableOptions = availableOptions.map(option => option.split('|'));

            let bestMatch = null;
            let bestMatchScore = 0;

            splittedAvailableOptions.forEach((splittedOption, index) => {
                let score = 0;

                splittedOption.forEach((part, partIndex) => {
                    if (partIndex < splittedPerfectMatches.length && part === splittedPerfectMatches[partIndex]) {
                        score++;
                    }
                });

                if (score > bestMatchScore) {
                    bestMatch = availableOptions[index];
                    bestMatchScore = score;
                }
            });

            const bestMatchIndex = availableOptions.findIndex((path) => path === bestMatch);

            return bestMatchIndex !== -1 && typeof availableOptions[bestMatchIndex] === 'string' ? bestMatchIndex : 0;
        },

        initializeVariables() {
            const defaultType = this.getTypeBy((item) => item.default);
            const defaultOptions = this.getDefaultOptionsFromCustomizations(this.customizations);

            if(!defaultType){
                throw Error(`Expected default type has not passed.`);
            }

            const indexes = this.getSelectedOptions(defaultOptions);
            const selected = [{ type: defaultType.type }, ...indexes];

            const filePath = this.getOptionPath(selected);

            const { text, files } = defaultType;

            this.selected = selected;
            this.typeText = text;

            this.handleSelectedUpdated(selected);

            this.$nextTick(() => {
                this.setBrandingPreference(this.userBrandingPreference);

                const updatedFilePath = this.getOptionPath(selected);

                this.worksheet = files[updatedFilePath];
            });

            return {files, filePath};
        },

        handleAlternativeToDisabledOption(object = { customization: '', option: '' }){
            const availableOptions = this.availableOptions.filter((path) => path.indexOf(object.option) >= 0);

            if(availableOptions.length === 0){
                this.$dispatch('notify', {
                    title: 'Oops!',
                    text: 'That option isn’t available right now. <span class="font-medium">How about trying a different one?</span>',
                    type: 'info'
                });

                throw Error(`Expected available option has not been found for ${object.option}.`);
            }

            const availableOptionIndex = this.getSimilarOptionBasedOnSelected(availableOptions, object);

            const availableOption = availableOptions[availableOptionIndex];

            const options = availableOption.split('|');

            options.forEach((option, index) => {
                if(index === 0){
                    this.setOption({ customization: 'type', option });

                    return
                }

                const customization = Object.keys(this.customizations)
                    .find(
                        (customizationKey, key) => this.customizations[customizationKey]
                            .find(
                                (customization) => customization.option === option)
                    );

                this.setOption({ customization, option });
            });
        },

        getSelectedOptions(options = []){
            return options.map((item) => {
                const key = Object.keys(item);
                return ({ [key]: item[key].option });
            });
        },

        getDefaultOptionsFromCustomizations(customizations){
            return Object.keys(customizations)
                .flatMap(
                    category => ({
                        [category]: customizations[category].find(option => option?.default === true)
                    })
                );
        },

        getOptionPath(customizations = []) {
            const { type } = this.getTypeProperty(customizations);

            let path = `${type}`;

            this.customizationsOrder.forEach((option) => {
                const property = customizations.find(customization => customization.hasOwnProperty(option));

                path += `-${property[option]}`
            });

            return path;
        },

        getTypeProperty(array){
            return array.find(item => item.hasOwnProperty('type'));
        },

        getTypeBy(callback = () => {}){
            return this.types.find(callback);
        },

        isPremiumOptionSelected(selected) {
            return selected.some((item) => {
                const isPremiumFromType = item['type'] && ['fillable', 'customizable', 'example'].includes(item['type']);

                const [key] = Object.keys(item);
                const selectedOption = typeof this.customizations[key] !== 'undefined' &&
                    this.customizations[key].find(({ option }) => item[key] === option);

                return isPremiumFromType || selectedOption?.premium;
            });
        },

        async openDownloadTab() {
            const newTab = window.open('', '_blank');

            if (!newTab) return null;

            try {
                const { data } = await api.get('loading-worksheet');

                newTab.document.open();
                newTab.document.writeln(data);
                newTab.document.close();
            } catch (_) { }

            return newTab;
        },

        closeDownloadTab(tab) {
            try {
                if (tab && !tab.closed) tab.close();
            } catch (_) { }
        },

        prepareDownloadOptions () {
            const selectedOptions = cloneDeep(this.selected);

            return selectedOptions.map((item) => {
                const [key] = Object.keys(item);
                if (key === 'language') {
                    const language = this.availableLanguages.find(({slug}) => slug === item[key]);
                    return {lang: language?.code ?? 'en'};
                }
                if (key === 'type') {
                    return {download_type: item[key]};
                }
                return {[key.toLowerCase()]: item[key]};
            }).reduce((acc, item) => ({...acc, ...item}), {});
        },

        async handleWorksheetDownloadStart() {
            if (this.loading) return;

            if(!this.worksheet) {
                this.$dispatch('notify', {
                    text: 'There was an error downloading your worksheet. Please refresh the page and try again.',
                    type: 'danger',
                });

                return;
            }

            const { url, option } = this.worksheet;
            const slug = this.content;

            const selected = this.prepareDownloadOptions();

            const { type } = this.getTypeProperty(this.selected);
            const parameters = { content_slug: slug, ...selected };
            const { branding } = selected;

            if (typeof branding === 'string' && branding !== '***therapist-aid***') {
                const newTab = await this.openDownloadTab();

                if (!newTab) {
                    this.pollForBranding({
                        slug,
                        option,
                        branding,
                        parameters,
                        fallbackToSameTab: true
                    });

                    return;
                }

                this.pollForBranding({
                    slug,
                    option,
                    branding,
                    parameters,
                    tab: newTab,
                    fallbackToSameTab: false
                });

                return;
            }

            if (type === 'standard' || type === 'example') {
                trackEvent('worksheet_download', 'Content', slug, parameters);

                const tab = window.open(url, '_blank');

                if (!tab) {
                    window.location.href = url;
                }

                return;
            }

            setTimeout(() => this.$dispatch(`show-${type}-instructions-modal`), 200);

            this.$dispatch('update-worksheet-data', { url, data: parameters });
        },

        async pollForBranding({ slug, option, branding, parameters, tab, fallbackToSameTab = false } = {}) {
            this.loading = true;
            this.times = 0;
            this.pollInFlight = false;
            this.downloadStarted = false;
            this.isPolling = false;
            this.currentPollRunId = (this.currentPollRunId || 0) + 1;

            const runId = this.currentPollRunId;

            if (this.interval) {
                clearInterval(this.interval);
            }

            const stop = () => {
                clearInterval(this.interval);

                this.times = 0;
                this.loading = false;
                this.pollInFlight = false;
            };

            const finishWithDownload = (downloadUrl) => {
                if (this.downloadStarted) return;

                this.downloadStarted = true;

                try {
                    if (this.interval) clearInterval(this.interval);
                } catch (_) { }

                this.times = 0;
                this.loading = false;
                this.pollInFlight = false;

                trackEvent('worksheet_download', 'Content', slug, parameters);

                if (tab && !tab.closed) {
                    tab.location.href = downloadUrl;

                    return;
                }

                if (fallbackToSameTab) {
                    window.location.href = downloadUrl;

                    return;
                }

                window.open(downloadUrl, '_blank');
            };

            this.interval = setInterval(async () => {
                if (runId !== this.currentPollRunId || this.downloadStarted || this.pollInFlight) return;

                this.pollInFlight = true;

                try {
                    this.times++;

                    if (this.times > 1 && !this.isPolling) {
                        this.isPolling = true;
                    }

                    if (this.times > 10) {
                        stop();

                        this.closeDownloadTab(tab);

                        return;
                    }

                    const { status, data } = await api.post('branding', {
                        worksheet: slug,
                        option: option,
                        branding: branding,
                        polling: this.isPolling,
                    });

                    if (runId !== this.currentPollRunId || this.downloadStarted) return;

                    if ([202, 208].includes(status)) {
                        return;
                    }

                    if (status === 200 && data?.url) {
                        this.isPolling = false;

                        finishWithDownload(data.url);

                        const checkAndCloseTab = () => {
                            try {
                                const checkDownloadStarted = () => {
                                    try {
                                        const isStillLoading = tab.document?.readyState !== 'complete';

                                        if (isStillLoading) {
                                            setTimeout(checkDownloadStarted, 1000);
                                        } else {
                                            if (tab && !tab.closed && tab.location.href !== data.url) {
                                                window.focus();
                                                tab.close();
                                            }
                                        }
                                    } catch (_) {
                                        setTimeout(() => {
                                            try {
                                                if (tab && !tab.closed && tab.location.href !== data.url) {
                                                    window.focus();
                                                    tab.close();
                                                }
                                            } catch (_) { }
                                        }, 2000);
                                    }
                                };

                                setTimeout(checkDownloadStarted, 1000);
                            } catch (_) { }
                        };

                        checkAndCloseTab();

                        return;
                    }

                    stop();
                } catch (error) {
                    if (runId !== this.currentPollRunId) return;

                    stop();

                    const status = error?.response?.status;

                    if (status === 423) {
                        this.$dispatch('notify', {
                            title: 'Just a moment!',
                            text: 'This file is being processed and should be available soon. Please try again shortly!',
                            type: 'info'
                        });
                    }

                    if (status === 408) {
                        this.$dispatch('notify', {
                            title: 'Sorry!',
                            text: 'We encountered an error while preparing your worksheet download. Please try again in a moment. <a class="font-medium text-primary-500 underline" href="#contact-us-modal">Contact support</a> if the problem persists.',
                            type: 'info'
                        });
                    }

                    if (status === 500) {
                        this.$dispatch('notify', {
                            title: 'Oops!',
                            text: 'There was an error generating your worksheet. Please try again later.',
                            type: 'danger'
                        });
                    }

                    this.closeDownloadTab(tab);

                    this.isPolling = false;
                } finally {
                    if (runId === this.currentPollRunId) {
                        this.pollInFlight = false;
                    }
                }
            }, 1500);
        }
    }
}

export default Download;
